import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Plus, Pencil, Trash2, Sparkles, FileText, Eye, Mail, Share2, Wand2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { RichTextEditor } from '../../components/RichTextEditor';
import { api, fileUrl } from '../../lib/api';
import { formatDate } from '../../components/Shared';
import { toast } from 'sonner';

const EMPTY = { title: '', period: '', body_html: '', status: 'draft', review_status: 'needs_review' };

export default function AdminReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [genNotes, setGenNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [suggestions, setSuggestions] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    api.get('/admin/reports').then((r) => setReports(r.data || [])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      if (editing?.id) await api.put(`/reports/${editing.id}`, form);
      else await api.post('/reports', form);
      toast.success('Report saved');
      setEditing(null);
      setSuggestions('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const generateDraft = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/admin/reports/generate-draft', { steve_notes: genNotes });
      toast.success('AI draft generated — review and edit before publishing');
      setGenOpen(false);
      setGenNotes('');
      load();
      setForm({ ...EMPTY, ...res.data });
      setEditing(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const makePdf = async (r) => {
    try {
      toast.info('Generating PDF…');
      const res = await api.post(`/admin/reports/${r.id}/pdf`);
      setPdfPreview(fileUrl(res.data.pdf_url));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PDF generation failed');
    }
  };

  const getSuggestions = async () => {
    setSuggesting(true);
    try {
      const plain = (form.body_html || '').replace(/<[^>]+>/g, ' ').slice(0, 5000);
      const res = await api.post('/admin/ai/assist', { action: 'report_suggestions', text: plain, context: `Report period: ${form.period}` });
      setSuggestions(res.data.result.text);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI suggestions failed');
    } finally {
      setSuggesting(false);
    }
  };

  const toNewsletter = async (r) => {
    try {
      await api.post(`/admin/reports/${r.id}/to-newsletter`);
      toast.success('Newsletter draft created from this report (see Newsletter → Broadcasts)');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const toSocial = async (r) => {
    try {
      const plain = (r.body_html || '').replace(/<[^>]+>/g, ' ').slice(0, 1200);
      await api.post('/admin/social/generate', { topic: `Monthly accountability report: ${r.title} (${r.period}). Key content: ${plain}`, link: '/reports' });
      toast.success('Facebook/social draft created (see Social Sharing)');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Social draft failed');
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/reports/${deleting.id}`);
      toast.success('Report deleted');
      setDeleting(null);
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div data-testid="admin-reports-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="font-serif font-black text-2xl text-[#1a2744]">Accountability Reports</h1>
        <div className="flex gap-2">
          <Button onClick={() => setGenOpen(true)} variant="outline" className="h-11 border-[#C9A84C] text-[#1a2744] font-bold btn-press" data-testid="report-generate-ai-button"><Sparkles className="h-4 w-4 mr-1.5 text-[#C9A84C]" /> AI Draft</Button>
          <Button onClick={() => { setForm(EMPTY); setEditing({}); setSuggestions(''); }} className="h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="report-add-button"><Plus className="h-4 w-4 mr-1.5" /> New Report</Button>
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-4">Monthly Ward 8 accountability reports. PDFs are generated server-side and never include private resident data — only public-safe summaries and aggregate totals.</p>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="space-y-3">
          {reports.length === 0 && <Card><CardContent className="p-8 text-center text-slate-500">No reports yet. Generate an AI draft or create one manually.</CardContent></Card>}
          {reports.map((r) => (
            <Card key={r.id} data-testid="admin-report-row">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-bold text-[#1a2744] flex-1 min-w-0 truncate">{r.title}</p>
                  <span className={`text-xs font-bold rounded-full px-2 py-0.5 border ${r.status === 'published' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>{r.status === 'published' ? 'Published' : 'Draft'}</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{r.period} · updated {formatDate(r.updated_at || r.created_at)}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button variant="outline" size="sm" className="font-bold h-10" onClick={() => { setForm({ ...EMPTY, ...r }); setEditing(r); setSuggestions(''); }} data-testid="report-edit-button"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                  <Button variant="outline" size="sm" className="font-bold h-10" onClick={() => makePdf(r)} data-testid="report-pdf-button"><FileText className="h-3.5 w-3.5 mr-1" /> {r.pdf_url ? 'Regenerate PDF' : 'Generate PDF'}</Button>
                  {r.pdf_url && <Button variant="outline" size="sm" className="font-bold h-10" onClick={() => setPdfPreview(fileUrl(r.pdf_url))} data-testid="report-pdf-preview-button"><Eye className="h-3.5 w-3.5 mr-1" /> View PDF</Button>}
                  <Button variant="outline" size="sm" className="font-bold h-10" onClick={() => toNewsletter(r)} data-testid="report-to-newsletter-button"><Mail className="h-3.5 w-3.5 mr-1" /> To Newsletter</Button>
                  <Button variant="outline" size="sm" className="font-bold h-10" onClick={() => toSocial(r)} data-testid="report-to-social-button"><Share2 className="h-3.5 w-3.5 mr-1" /> Social Draft</Button>
                  <Button variant="outline" size="sm" className="font-bold h-10 text-red-600 border-red-200" onClick={() => setDeleting(r)} data-testid="report-delete-button"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* AI generate dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent data-testid="report-generate-dialog">
          <DialogHeader><DialogTitle className="font-serif text-[#1a2744]">Generate AI report draft</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">The AI uses only public-safe aggregate statistics from the Ward 8 Public Issue Tracker — never names, addresses, or private notes. You review and edit before anything is published.</p>
          <Textarea value={genNotes} onChange={(e) => setGenNotes(e.target.value)} rows={4} placeholder="Steve's notes to include (optional)…" data-testid="report-generate-notes" />
          <Button onClick={generateDraft} disabled={generating} className="h-12 bg-[#1a2744] text-white font-bold btn-press w-full" data-testid="report-generate-confirm">
            {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />} Generate Draft
          </Button>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="report-edit-dialog">
          <DialogHeader><DialogTitle className="font-serif text-[#1a2744]">{editing?.id ? 'Edit Report' : 'New Report'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Title</label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-11" data-testid="report-title-input" />
              </div>
              <div>
                <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Period</label>
                <Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="e.g. July 2026" className="h-11" data-testid="report-period-input" />
              </div>
            </div>
            <div>
              <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Report content</label>
              <RichTextEditor value={form.body_html} onChange={(v) => setForm({ ...form, body_html: v })} />
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2">Privacy rule: never paste resident names, emails, phone numbers, exact private addresses, private notes, or unapproved photos into report text.</p>
            </div>
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-40">
                <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="h-11" data-testid="report-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft (hidden from public)</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="button" variant="outline" onClick={getSuggestions} disabled={suggesting} className="h-11 border-[#C9A84C] text-[#1a2744] font-bold btn-press" data-testid="report-ai-suggestions-button">
                {suggesting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Wand2 className="h-4 w-4 mr-1.5 text-[#C9A84C]" />} AI Suggestions
              </Button>
            </div>
            {suggestions && (
              <div className="bg-[#EAF2FF] border border-[#CFE0FF] rounded-xl p-4" data-testid="report-ai-suggestions">
                <p className="font-bold text-[#1a2744] text-sm mb-1">AI editing suggestions</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{suggestions}</p>
              </div>
            )}
            <div className="flex gap-3">
              <Button onClick={save} disabled={busy} className="flex-1 h-12 bg-[#1a2744] text-white font-bold btn-press" data-testid="report-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Save Report</Button>
              <Button variant="outline" onClick={() => setEditing(null)} className="h-12 font-bold">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF preview popup */}
      <Dialog open={!!pdfPreview} onOpenChange={(o) => !o && setPdfPreview(null)}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col" data-testid="report-pdf-preview-dialog">
          <DialogHeader><DialogTitle className="font-serif text-[#1a2744]">PDF Preview</DialogTitle></DialogHeader>
          {pdfPreview && <iframe src={pdfPreview} title="Report PDF" className="flex-1 w-full rounded-lg border" />}
          <Button asChild variant="outline" className="h-11 font-bold"><a href={pdfPreview} target="_blank" rel="noopener noreferrer" data-testid="report-pdf-download-link">Open / Download PDF</a></Button>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="report-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this report?</AlertDialogTitle>
            <AlertDialogDescription>This will remove it from the public site too. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="report-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
